import { useContext } from "react"
import { db } from '../../../firebase/config'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import { Auth } from '../../../StateManagment/Auth'
import recordStatusChange from '../../../helpers/statusHistory/recordStatusChange'
import { computeFailedStreakDetails } from '../../../helpers/contactMoments/computeFailedStreak'
import isUnreachableEligible from '../../../helpers/contactMoments/isUnreachableEligible'
import ReadOnlyGuard from '../../common/ReadOnlyGuard'

/**
 * De enige route naar de status 'Onbereikbaar'. Verschijnt pas na drie
 * mislukte pogingen op rij én zodra de door de tenant ingestelde wachttijd is
 * verstreken, geteld vanaf de eerste mislukte poging van die reeks.
 *
 * De status verandert nog steeds pas na een klik — geen automatische
 * schrijfactie op de achtergrond (ADR-019).
 *
 * `afterDays` komt als prop binnen in plaats van uit `useSettings()`. Twee
 * redenen: die hook opent drie listeners (waaronder de complete MSI-effect-
 * lijst) en deze knop is altijd gemount omdat het tabblad met `display: none`
 * verborgen wordt — én de hook start op de standaard van 14 zolang hij laadt,
 * waardoor de knop bij een tenant met 30 of 60 dagen kortstondig te vroeg
 * verscheen. `null` betekent "nog niet bekend": dan geen knop.
 *
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 *
 * @param {object} props
 * @param {Array<object>} props.contactMoments - Contactmomenten van dit huishouden, nieuwste eerst
 * @param {string} props.adressID - Adresses.ID van het huishouden (voor de statushistorie)
 * @param {string} props.adressDocid - Firestore-docid van het huishouden
 * @param {string} props.currentStatus - Huidige Adresses.Status
 * @param {number|null} [props.afterDays] - Wachttijd in dagen; null zolang de instellingen laden
 */
const MarkUnreachableButton = ({ contactMoments, adressID, adressDocid, currentStatus, afterDays = null }) => {
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // Poort valt dicht zolang de wachttijd onbekend is, nooit open.
    if (afterDays === null || afterDays === undefined) return null

    const { streak, firstFailedAt } = computeFailedStreakDetails(contactMoments)

    if (!isUnreachableEligible({ streak, firstFailedAt, afterDays })) return null
    if (currentStatus === 'unreachable') return null
    if (!adressDocid) return null

    const markUnreachable = async () => {
        await db.collection('Adresses').doc(adressDocid).update({ Status: 'unreachable' })
        await recordStatusChange({
            entityType: 'adress',
            entityID: adressID,
            fromStatus: currentStatus,
            toStatus: 'unreachable',
            changedBy: auth?.ID,
        })
        setSaved('flex')
    }

    return (
        <ReadOnlyGuard edit="addresses.edit">
            <div className="mark-unreachable-container">
                <button className="mark-unreachable-button" onClick={markUnreachable}>
                    Markeer als onbereikbaar
                </button>
            </div>
        </ReadOnlyGuard>
    )
}

export default MarkUnreachableButton
